<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:17805: the balance card 12 under the logo row, the question 24 under it and the rows
			     16 under the question. Paid in full (6847:86679) has no card: the question 24 under the logo row. -->
			<LayoutFlex
				:padding="upfront ? 'var(--size-24) var(--size-20)' : 'var(--size-12) var(--size-20) var(--size-24)'"
				gap="var(--size-24)"
			>
				<BalanceCard v-if="!upfront" :title="balanceTitle" :description="balanceDescription">
					<SupplyProgress v-if="q.progress" :paid="q.progress.paid" :total="q.progress.total" />
				</BalanceCard>

				<LayoutFlex padding="0" gap="var(--size-16)">
					<Text font="--heading-h3" color="--text-dark-primary">When would you like to cancel your Kova plan?</Text>

					<!-- Paid in full (Figma 6847:86679): nothing is owed — the period-end cancel or the refund. -->
					<LayoutFlex v-if="upfront" padding="0" gap="var(--size-8)" min-width="100%">
						<OptionRow
							v-if="q.endOfPeriod"
							title="Cancel after my current plan ends"
							:description="`Your plan stays active until ${formatDate(q.endOfPeriod.access_until, 'medium')}. You’ll receive your remaining orders, and it won’t renew after that.`"
							:disabled="busy"
							@select="cancelAtPeriodEnd"
						/>
						<OptionRow
							v-if="q.refund"
							title="Cancel right now"
							description="Your treatment and all upcoming orders will stop"
							:disabled="busy"
							@select="goRefund"
						/>
					</LayoutFlex>

					<LayoutFlex v-else padding="0" gap="var(--size-8)" min-width="100%">
						<OptionRow
							v-if="q.endToday"
							title="Cancel right now"
							:description="endTodayDescription"
							:amount="money(q.endToday.amount)"
							:disabled="busy"
							@select="goEndNow"
						/>
						<OptionRow
							v-if="q.supplyEnd"
							:title="`Cancel after ${pluralize(installments.length, 'scheduled payment')}`"
							:description="afterPaymentsDescription"
							:amount="money(0)"
							:disabled="busy"
							@select="goEndAfter"
						/>
						<OptionRow
							v-if="q.keep"
							title="Keep my plan, but don’t renew it"
							:description="`Your plan stays active until ${formatDate(q.keep.access_until)}. You’ll get your remaining orders, and it won’t renew after that.`"
							:amount="money(0)"
							:disabled="busy"
							@select="keepButDontRenew"
						/>
					</LayoutFlex>
				</LayoutFlex>
			</LayoutFlex>
		</template>
	</LayoutScreen>
	<!-- The cancel call failed (500 / no connection): nothing was cancelled. "Try again" re-runs it here. -->
	<Modal :visible="cancelFailed" fullscreen animation="none">
		<CancelErrorScreen :step="step.title" @retry="retryCancel" />
	</Modal>
</template>

<script setup lang="ts">
// /cancel/commitment/options — Figma `kova cancel flow3` 6579:9648, now 7415:17805: the exit options for an approved
// commitment mid-term. One row per option the backend offers right now (`cancellation_options`,
// via `flow.quotes`): `instant_fee` → "Cancel right now", `installments_fee` → "Cancel after N
// scheduled payments" (not on 3-month plans, the last payment of a supply or the last supply),
// `end_of_commitment` → "Keep my plan". A paid-in-full plan (`flow.kind === 'upfront'`, Figma
// 6847:86679) owes nothing: `end_of_period` → "Cancel after my current plan ends" (a plain `soft`),
// `upfront_refund` → "Cancel right now" (the refund screen). The subscription is re-read on entry —
// amounts move with every charge and shipment, and the backend reprices at call time.
//
// Analytics: WHEN_TO_CANCEL (AL-15098) with `data.commitment`; the answers are `cancel_right_now`,
// `cancel_after_scheduled_payments` and `cancel_future_renewals_only` ("Keep my plan" / "after my current
// plan ends").
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { LayoutFlex, LayoutScreen, Modal, Text } from '@simple-life-app/growth-ui-kova'
import CancelErrorScreen from '@/components/cancel/CancelErrorScreen.vue'
import CancelHeader from '@/components/CancelHeader.vue'
import BalanceCard from '@/components/cancel/BalanceCard.vue'
import OptionRow from '@/components/cancel/OptionRow.vue'
import SupplyProgress from '@/components/cancel/SupplyProgress.vue'
import { COMMITMENT_ROUTES, feeBreakdown } from '@/cancelFlow/commitment'
import { formatDate, formatMoney, joinDates, pluralize } from '@/cancelFlow/format'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import { STEP_TITLE, commitmentStepData, stateChangeOnCancel, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const flow = useCancelFlowStore()
const subs = useSubscriptionStore()
const survey = useSurveyStore()

const q = computed(() => flow.quotes)
const upfront = computed(() => flow.kind === 'upfront')
const busy = ref(false)
const cancelFailed = ref(false)
/** The cancel that failed — "Try again" re-runs this one. */
let lastCancel: () => Promise<void> = keepButDontRenew

function money(amount: number | null | undefined) {
	return formatMoney(amount, q.value.currency)
}

const installments = computed(() => q.value.supplyEnd?.installments ?? [])

// "You’ve received 3 months of medication and paid for 1." — from how `instant_fee` was priced; left
// out when the backend didn't send the breakdown.
const balanceDescription = computed(() => {
	const tail = 'Your balance reflects the medication you’ve already received.'
	const b = feeBreakdown(q.value.endToday, q.value.monthlyPrice)
	if (!b) return tail
	return `You’ve received ${pluralize(b.shippedMonths, 'month')} of medication and paid for ${b.paidMonths}. ${tail}`
})

// The cycle paid past what shipped — a refill in review, its charge counted before its supply (PR #23
// review, 2026-10-23): billing sends `instant_fee` as `amount: 0` + `refund`. No Figma frame; the refund
// reads as on the paid-in-full screens.
const refundToday = computed(() => q.value.endToday?.refund ?? 0)

const balanceTitle = computed(() =>
	refundToday.value > 0
		? `You’ll get ${money(refundToday.value)} back if you end today`
		: `Your balance to end today is ${money(q.value.endToday?.amount)}`,
)

const endTodayDescription = computed(() =>
	refundToday.value > 0
		? `No more payments or deliveries. You’ll get ${money(refundToday.value)} back`
		: 'No more payments or deliveries',
)

const afterPaymentsDescription = computed(() => {
	const list = installments.value
	if (!list.length) return ''
	return `Pay ${money(list[0].amount)} on ${joinDates(list.map((i) => i.date))}. Your plan ends after your final payment.`
})

const step = useCancelStepLogging(STEP_TITLE.WHEN_TO_CANCEL, {
	getCommitment: () =>
		upfront.value
			? commitmentStepData(subs.subscription, flow.kind, q.value.refund, { fee: 0 })
			: commitmentStepData(subs.subscription, flow.kind, q.value.endToday, { fee: q.value.endToday?.amount ?? 0 }),
})

onMounted(() => subs.refreshSubscription())

function goEndNow() {
	step.record(['cancel_right_now'])
	router.push(COMMITMENT_ROUTES.endNow)
}

function goEndAfter() {
	step.record(['cancel_after_scheduled_payments'])
	router.push(COMMITMENT_ROUTES.endAfter)
}

function goRefund() {
	step.record(['cancel_right_now'])
	router.push(COMMITMENT_ROUTES.refund)
}

/** "Keep my plan, but don't renew it" — a plain `soft`; the backend winds the commitment down. */
async function keepButDontRenew() {
	if (busy.value) return
	busy.value = true
	lastCancel = keepButDontRenew
	step.record(['cancel_future_renewals_only'])
	const { price, currency, keep } = q.value
	const changed = stateChangeOnCancel('soft_commitment')
	try {
		await subs.cancel({ cancellationType: 'soft' })
		changed()
		const until = subs.subscription?.expired_at ?? keep?.access_until ?? null
		flow.setOutcome({ kind: 'renewal_off', price, currency, until })
		survey.reset()
		router.push('/cancel/done')
	} catch {
		cancelFailed.value = true
	} finally {
		busy.value = false
	}
}

/**
 * Paid in full: "Cancel after my current plan ends" — a plain `soft`, access to the period end + 14
 * days. The refund exit is gone after it (the engine offers it only over no cancel).
 */
async function cancelAtPeriodEnd() {
	if (busy.value) return
	busy.value = true
	lastCancel = cancelAtPeriodEnd
	step.record(['cancel_future_renewals_only'])
	const { endOfPeriod } = q.value
	const changed = stateChangeOnCancel('soft')
	try {
		await subs.cancel({ cancellationType: 'soft' })
		changed()
		flow.setOutcome({ kind: 'paid_through', until: subs.subscription?.expired_at ?? endOfPeriod?.access_until ?? null })
		survey.reset()
		router.push('/cancel/done')
	} catch {
		cancelFailed.value = true
	} finally {
		busy.value = false
	}
}

function retryCancel() {
	cancelFailed.value = false
	lastCancel()
}
</script>
